import type { Step } from 'react-joyride';

/**
 * Flusso tour desktop:
 * - Step "interactive": l'utente DEVE cliccare l'elemento per avanzare.
 *   Il click naviga realmente alla pagina corrispondente.
 * - Step "explain": spiega la pagina corrente, con bottone "Avanti".
 *
 * Ogni step interattivo è seguito da uno step esplicativo sulla pagina di destinazione.
 */

export const TOUR_STEPS: Step[] = [
  // 0 - Benvenuto
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Benvenuto su Vinum!',
    content:
      'Ti guideremo alla scoperta della piattaforma passo dopo passo. Segui le istruzioni e clicca dove indicato.',
    data: { interactive: false },
  },
  // 1 - Overview sidebar
  {
    target: '[data-tour="sidebar-nav"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Navigazione',
    content:
      'Questa è la barra di navigazione. Ora ti guideremo in ogni sezione. Clicca sugli elementi quando richiesto.',
    data: { interactive: false },
  },
  // 2 - Click Home
  {
    target: '[data-tour="nav-home"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Home',
    content: 'Clicca su Home per visualizzare la pagina principale.',
    data: { interactive: true },
  },
  // 3 - Explain Home
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'La tua Home',
    content:
      'Qui trovi i vini in evidenza, le novità e i prodotti consigliati per te. Puoi esplorare le collezioni e accedere rapidamente alle sezioni principali.',
    data: { interactive: false },
  },
  // 4 - Click Catalogo
  {
    target: '[data-tour="nav-catalog"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Catalogo',
    content: 'Ora clicca su Catalogo per esplorare i nostri vini.',
    data: { interactive: true },
  },
  // 5 - Explain Catalogo
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'Il Catalogo',
    content:
      'Qui puoi cercare vini per nome, filtrare per categoria, tipo, prezzo e molto altro. Clicca su un prodotto per vederne i dettagli e aggiungerlo al carrello.',
    data: { interactive: false },
  },
  // 6 - Click Carrello
  {
    target: '[data-tour="nav-cart"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Carrello',
    content: 'Clicca sul Carrello per vedere i tuoi prodotti.',
    data: { interactive: true },
  },
  // 7 - Explain Carrello
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'Il tuo Carrello',
    content:
      'Qui trovi tutti i prodotti che hai aggiunto. Puoi modificare le quantità, rimuovere articoli e procedere al checkout per completare l\'ordine.',
    data: { interactive: false },
  },
  // 8 - Click Ordini
  {
    target: '[data-tour="nav-orders"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Ordini',
    content: 'Clicca su Ordini per consultare i tuoi acquisti.',
    data: { interactive: true },
  },
  // 9 - Explain Ordini
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'I tuoi Ordini',
    content:
      'Qui trovi lo storico completo dei tuoi ordini: stato, dettagli e tracking. Puoi anche riordinare i tuoi acquisti preferiti.',
    data: { interactive: false },
  },
  // 10 - Click Cantina
  {
    target: '[data-tour="nav-winery"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Cantina',
    content: 'Clicca su Cantina per gestire la tua azienda vinicola.',
    data: { interactive: true },
  },
  // 11 - Explain Cantina
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'La tua Cantina',
    content:
      'Qui puoi gestire i dettagli della tua azienda, i prodotti e le informazioni della cantina.',
    data: { interactive: false },
  },
  // 12 - Click Statistiche
  {
    target: '[data-tour="nav-statistics"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Statistiche',
    content: 'Clicca su Statistiche per analizzare vendite e prodotti.',
    data: { interactive: true },
  },
  // 13 - Explain Statistiche
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'Le tue Statistiche',
    content:
      'Qui trovi grafici e dati su vendite e prodotti. Puoi monitorare le performance della tua cantina nel tempo.',
    data: { interactive: false },
  },
  // 14 - Click Profilo
  {
    target: '[data-tour="nav-profile"]',
    placement: 'right',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Profilo',
    content: 'Infine, clicca su Profilo per le impostazioni.',
    data: { interactive: true },
  },
  // 15 - Explain Profilo + Fine
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'Il tuo Profilo',
    content:
      'Da qui gestisci i dati personali, le preferenze, le notifiche e puoi rivisitare questo tutorial in qualsiasi momento. Sei pronto a iniziare!',
    data: { interactive: false },
  },
];

export const MOBILE_TOUR_STEPS: Step[] = [
  // 0 - Benvenuto
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Benvenuto su Vinum!',
    content:
      'Ti guideremo alla scoperta della piattaforma. Segui le istruzioni e tocca dove indicato.',
    data: { interactive: false },
  },
  // 1 - Click Menu hamburger
  {
    target: '[data-tour="mobile-menu-button"]',
    placement: 'bottom',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Menu',
    content: 'Tocca qui per aprire il menu completo.',
    data: { interactive: true },
  },
  // 2 - Bottom nav
  {
    target: '[data-tour="mobile-nav"]',
    placement: 'top',
    disableBeacon: true,
    spotlightClicks: true,
    title: 'Navigazione',
    content:
      'Usa la barra in basso per spostarti tra le sezioni. Tocca Catalogo per continuare.',
    data: { interactive: true },
  },
  // 3 - Explain Catalogo
  {
    target: '[data-tour="page-content"]',
    placement: 'top-start',
    disableBeacon: true,
    title: 'Il Catalogo',
    content:
      'Cerca vini, filtra per categoria e tocca un prodotto per i dettagli. Sei pronto!',
    data: { interactive: false },
  },
];
